import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Globe, User, LogOut, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

const MoonRiverLogo = ({ className = "", size = "default" }) => {
  const sizeClasses = {
    small: "h-10 lg:h-12",
    default: "h-14 lg:h-16",
    large: "h-20 lg:h-24"
  };

  return (
    <img
      src="/logo-full-transparent.png"
      alt="Moon River Real Estate"
      className={`${sizeClasses[size]} w-auto object-contain ${className}`}
    />
  );
};

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const isLandingPage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/#about', label: t('nav.about') },
    { href: '/#portfolio', label: t('nav.portfolio') },
    { href: '/#contact', label: t('nav.contact') },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname + location.hash === href;
  };

  // On landing page: transparent at top with dark text (white hero), glass on scroll
  // On other pages: always glass with light text (dark background)
  const atTopOnLanding = isLandingPage && !isScrolled;

  const textColor = atTopOnLanding ? 'text-[#1A1A1A]' : 'text-[#FFFFFF]';
  const activeColor = 'text-[#E5B1A1]';
  const hoverColor = 'hover:text-[#E5B1A1]';

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        atTopOnLanding
          ? 'bg-transparent py-6'
          : 'glass py-3'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="logo">
            <MoonRiverLogo />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`text-sm font-medium tracking-wide transition-colors duration-300 ${
                  isActive(link.href) ? activeColor : `${textColor} ${hoverColor}`
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-2 text-sm ${textColor} ${hoverColor} transition-colors`}
              data-testid="language-toggle"
            >
              <Globe size={18} strokeWidth={1.5} />
              <span className="hidden sm:inline uppercase">{language === 'fr' ? 'FR' : language === 'en' ? 'EN' : 'PT'}</span>
            </button>

            {user ? (
              <div className="hidden lg:flex items-center gap-4">
                <Link
                  to="/dashboard"
                  className={`flex items-center gap-2 text-sm ${textColor} ${hoverColor} transition-colors`}
                  data-testid="dashboard-link"
                >
                  <LayoutDashboard size={18} strokeWidth={1.5} />
                  <span>{t('nav.dashboard')}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className={`flex items-center gap-2 text-sm ${textColor} ${hoverColor} transition-colors`}
                  data-testid="logout-button"
                >
                  <LogOut size={18} strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="hidden lg:flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-[#FFFFFF] transition-all duration-300 hover:brightness-110"
                style={{ backgroundColor: '#E5B1A1' }}
                data-testid="login-button"
              >
                <User size={18} strokeWidth={1.5} />
                <span>{t('nav.clientArea')}</span>
              </Link>
            )}

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 ${textColor}`}
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass mt-2"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col py-6 px-6 gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-lg font-medium transition-colors ${
                    isActive(link.href) ? 'text-[#E5B1A1]' : 'text-[#A3A3A0] hover:text-[#F5F5F0]'
                  }`}
                >
                  {link.label}
                </a>
              ))}
              <div className="border-t border-[rgba(255,255,255,0.08)] my-2" />
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 text-lg text-[#A3A3A0] hover:text-[#F5F5F0]"
                  >
                    <LayoutDashboard size={20} strokeWidth={1.5} />
                    <span>{t('nav.dashboard')}</span>
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                    className="flex items-center gap-2 text-lg text-[#A3A3A0] hover:text-[#F5F5F0]"
                  >
                    <LogOut size={20} strokeWidth={1.5} />
                    <span>{t('nav.logout')}</span>
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="btn-primary text-center"
                >
                  {t('nav.clientArea')}
                </Link>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
